/**
 * Teach it once (#1738): make what the office just learned a rule, so every
 * matching job carries it as a Finish Line promise and nobody has to remember.
 *
 *   what   a title and an optional detail (or, for delivery, how the packet goes)
 *   when   the phase it's owed in          (a checklist rule only)
 *   who    the office or the tech          (a checklist rule only)
 *   where  for every job at this customer / site / water authority / job type,
 *          each with its real name and how many open jobs it lands on
 *
 * The confirm line says how many open jobs it applies to right away. One
 * filled button ("Teach it"); everything else is quiet. `TeachRuleForm` is the
 * body on its own so tests and the render proof can draw it without a modal.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { PHASES, type DeliveryRoute, type Phase, type RuleKind, type RuleScope } from "@/lib/finish-line";
import {
    ROUTE_CHOICES,
    appliesToWords,
    defaultTitle,
    scopeLead,
    useCreateRule,
    useRuleTargets,
    type NewRule,
    type RuleTargets,
} from "@/lib/promise-rules";
import { PHASE_LABEL } from "./finishLineModel";

export interface TeachPreset {
    kind?: RuleKind;
    phase?: Phase;
}

const KIND_CHOICES: { kind: RuleKind; label: string; hint: string }[] = [
    { kind: "checklist", label: "Something to do", hint: "Stays open on each job until someone marks it done." },
    { kind: "delivery_route", label: "How the packet goes", hint: "Their portal, mail, or one person, not our email." },
    { kind: "stamp_required", label: "Store stamp required", hint: "A skip reason won't keep the stamp promise." },
];

const radio = "h-4 w-4 shrink-0 accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function TeachRuleForm({
    jobId,
    targets,
    preset,
    saving = false,
    onSave,
    onCancel,
}: {
    jobId: string;
    targets: RuleTargets;
    preset?: TeachPreset;
    saving?: boolean;
    onSave: (rule: NewRule, appliesTo: string) => void;
    onCancel: () => void;
}) {
    const kinds = KIND_CHOICES.filter((k) => k.kind !== "stamp_required" || targets.vendorWo);
    const [kind, setKind] = useState<RuleKind>(preset?.kind ?? "checklist");
    const [title, setTitle] = useState("");
    const [detail, setDetail] = useState("");
    const [phase, setPhase] = useState<Phase>(preset?.phase ?? "before_visit");
    const [owner, setOwner] = useState<"office" | "tech">("office");
    const [blocksBooking, setBlocksBooking] = useState(false);
    const [route, setRoute] = useState<DeliveryRoute>("portal");
    const [routeLabel, setRouteLabel] = useState("");
    const [scope, setScope] = useState<RuleScope | null>(targets.targets[0]?.scopeType ?? null);

    const target = targets.targets.find((t) => t.scopeType === scope) ?? null;
    const fallbackTitle = target ? defaultTitle(kind, target.name, route) : "";
    const finalTitle = title.trim() || fallbackTitle;
    const complaint = !target ? "This job has nothing to attach a rule to." : !finalTitle ? "Say what to do." : null;
    const applies = target ? appliesToWords(target) : "";

    const submit = () => {
        if (complaint || saving || !target) return;
        onSave(
            {
                scopeType: target.scopeType,
                scopeId: target.scopeId,
                kind,
                phase: kind === "checklist" ? phase : kind === "delivery_route" ? "after_visit" : "on_site",
                owner: kind === "checklist" ? owner : "office",
                title: finalTitle,
                detail: detail.trim() || null,
                config:
                    kind === "delivery_route"
                        ? { route, ...(routeLabel.trim() ? { label: routeLabel.trim() } : {}) }
                        : kind === "checklist" && phase === "before_visit" && blocksBooking
                          ? { blocksBooking: true }
                          : {},
                sourceJobId: jobId,
            },
            applies,
        );
    };

    return (
        <div className="grid gap-5 text-sm" data-testid="teach-rule-form">
            <p className="m-0 text-[13px] text-muted-foreground">
                Make it a rule once, and every matching job carries it on its Finish Line.
            </p>

            {kinds.length > 1 && (
                <fieldset className="grid gap-2">
                    <legend className="mb-1.5 text-[13px] font-semibold text-foreground">What kind of rule</legend>
                    {kinds.map((k) => (
                        <label key={k.kind} className="flex items-baseline gap-2.5">
                            <input
                                type="radio"
                                name="teach-kind"
                                className={radio}
                                checked={kind === k.kind}
                                onChange={() => setKind(k.kind)}
                            />
                            <span>
                                <span className="font-medium text-foreground">{k.label}</span>
                                <span className="block text-[13px] text-muted-foreground">{k.hint}</span>
                            </span>
                        </label>
                    ))}
                </fieldset>
            )}

            {kind === "delivery_route" && (
                <fieldset className="grid gap-2" data-testid="teach-route">
                    <legend className="mb-1.5 text-[13px] font-semibold text-foreground">The packet goes by</legend>
                    {ROUTE_CHOICES.map((r) => (
                        <label key={r.route} className="flex items-center gap-2.5">
                            <input
                                type="radio"
                                name="teach-route"
                                className={radio}
                                checked={route === r.route}
                                onChange={() => setRoute(r.route)}
                            />
                            <span className="text-foreground">{r.label}</span>
                        </label>
                    ))}
                    <Input
                        aria-label={route === "portal" ? "Which portal" : route === "mail" ? "Mail to" : "Who"}
                        className="mt-1 text-sm"
                        value={routeLabel}
                        placeholder={ROUTE_CHOICES.find((r) => r.route === route)!.placeholder}
                        onChange={(e) => setRouteLabel(e.target.value)}
                    />
                </fieldset>
            )}

            <div className="grid gap-1.5">
                <Label htmlFor="teach-title" className="text-[13px] font-semibold">
                    {kind === "checklist" ? "What to do" : "Name it"}
                </Label>
                <Input
                    id="teach-title"
                    className="text-sm"
                    value={title}
                    maxLength={200}
                    placeholder={fallbackTitle || "Get the gate key from the custodian before the visit"}
                    onChange={(e) => setTitle(e.target.value)}
                />
                <Label htmlFor="teach-detail" className="mt-2 text-[13px] font-semibold">
                    Detail <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                    id="teach-detail"
                    className="min-h-[64px] text-sm"
                    value={detail}
                    maxLength={1000}
                    placeholder="Why, or how. Shows under the promise on every job."
                    onChange={(e) => setDetail(e.target.value)}
                />
            </div>

            {kind === "checklist" && (
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="grid gap-1.5">
                        <Label htmlFor="teach-phase" className="text-[13px] font-semibold">
                            When
                        </Label>
                        <select
                            id="teach-phase"
                            className="h-9 rounded-md border bg-background px-2 text-sm"
                            value={phase}
                            onChange={(e) => setPhase(e.target.value as Phase)}
                        >
                            {PHASES.map((p) => (
                                <option key={p} value={p}>
                                    {PHASE_LABEL[p]}
                                </option>
                            ))}
                        </select>
                        {phase === "before_visit" && (
                            <label className="mt-1 flex items-center gap-2 text-[13px] text-foreground/80">
                                <input
                                    type="checkbox"
                                    className={radio}
                                    checked={blocksBooking}
                                    onChange={(e) => setBlocksBooking(e.target.checked)}
                                />
                                Booking waits on this
                            </label>
                        )}
                    </div>
                    <fieldset className="grid gap-1.5">
                        <legend className="mb-1.5 text-[13px] font-semibold text-foreground">Who does it</legend>
                        {(["office", "tech"] as const).map((o) => (
                            <label key={o} className="flex items-center gap-2.5">
                                <input
                                    type="radio"
                                    name="teach-owner"
                                    className={radio}
                                    checked={owner === o}
                                    onChange={() => setOwner(o)}
                                />
                                <span className="text-foreground">{o === "office" ? "The office" : "The tech"}</span>
                            </label>
                        ))}
                    </fieldset>
                </div>
            )}

            <fieldset className="grid gap-2" data-testid="teach-scope">
                <legend className="mb-1.5 text-[13px] font-semibold text-foreground">For every job at</legend>
                {targets.targets.map((t) => (
                    <label key={t.scopeType} className="flex items-baseline gap-2.5">
                        <input
                            type="radio"
                            name="teach-scope"
                            className={radio}
                            checked={scope === t.scopeType}
                            onChange={() => setScope(t.scopeType)}
                        />
                        <span className="min-w-0">
                            <span className="text-muted-foreground">{scopeLead(t.scopeType)}: </span>
                            <span className="font-semibold text-foreground">{t.name}</span>
                            <span className="text-[13px] text-muted-foreground"> · {t.openJobs} open</span>
                        </span>
                    </label>
                ))}
            </fieldset>

            <div className="grid gap-3 border-t pt-4">
                <p
                    className={cn("m-0 text-sm font-semibold", complaint ? "text-destructive" : "text-foreground")}
                    data-testid="teach-applies"
                >
                    {complaint ?? applies}
                </p>
                <div className="flex flex-wrap items-center justify-end gap-4">
                    <button
                        type="button"
                        className="rounded-sm text-sm font-semibold text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                    <Button type="button" disabled={!!complaint || saving} onClick={submit}>
                        {saving ? "Teaching…" : "Teach it"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

/** The dialog: reads the job's targets when it opens, saves through `POST /api/promise-rules`. */
export function TeachRuleDialog({
    open,
    onClose,
    jobId,
    preset,
}: {
    open: boolean;
    onClose: () => void;
    jobId: string;
    preset?: TeachPreset;
}) {
    const q = useRuleTargets(jobId, open);
    const create = useCreateRule();
    // A fresh form each time it opens (a stale title is how a rule lands on the wrong thing).
    const [round, setRound] = useState(0);
    useEffect(() => {
        if (open) setRound((n) => n + 1);
    }, [open]);
    const key = useMemo(() => `${round}:${preset?.kind ?? ""}:${preset?.phase ?? ""}`, [round, preset]);

    return (
        <Dialog open={open} onClose={onClose} title="Teach it once">
            {q.isError ? (
                <p className="text-sm text-destructive">Couldn't load this job's customer, site and city. Try again.</p>
            ) : !q.data ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
                <TeachRuleForm
                    key={key}
                    jobId={jobId}
                    targets={q.data}
                    preset={preset}
                    saving={create.isPending}
                    onCancel={onClose}
                    onSave={(rule) =>
                        create.mutate(rule, {
                            onSuccess: (res) => {
                                const applies = appliesToWords({
                                    scopeType: res.rule.scopeType,
                                    name: res.rule.scopeName ?? "matching",
                                    openJobs: res.openJobs,
                                }).replace(/\.$/, "");
                                toast.success(`Taught: “${res.rule.title}”. ${applies}.`);
                                onClose();
                            },
                            onError: (e) => toast.error(`That rule didn't save: ${e.message}`),
                        })
                    }
                />
            )}
        </Dialog>
    );
}
